import { useRef } from "react";
import { useAppContext } from "../../../../AppContext";
import { PaymentTypes } from "../../../../common/enums";
import { PaymentTypesLabels } from "../../../../common/labels";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  getErrorToast,
  getUndoToast,
} from "../../../../common/infrastructure/utilities/toastUtility";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";
import { formatPrice } from "../../../../common/utilities/currencyUtility";
import { IPlannerEventModel } from "../models/plannerEventModel";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { buildQuickArrivalRequest } from "../utils/quickArrivalUtility";

/**
 * The one-tap arrival on an attendee row (studio setting "Бързо отбелязване на
 * присъствие"): marks the client arrived with their plan for the class, or with the
 * studio's first payment type at the class price, with no arrival page; a toast says what
 * was used and offers to take it back.
 */
export const usePlannerEventQuickArrival = (
  event?: IPlannerEventModel,
  studioSettings?: IStudioSettingsModel,
) => {
  const { selectedStudio } = useAppContext();
  const { mutateAsync } = useApiMutation();
  // Reservations sent and not undone: the bolt stays on the row until the class reloads,
  // and a second tap there would only get "already confirmed" (20707).
  const sentUserEventIds = useRef(new Set<number>());

  // The payment types come from a separate request: no bolt until they are here, or a tap
  // would wrongly say the studio has none.
  const isQuickArrivalEnabled =
    !!selectedStudio?.enableQuickArrival && !!studioSettings;

  const undoArrival = async (user: IPlannerEventUserModel) => {
    try {
      await mutateAsync({
        endpoint: `${endpointsMetadata.planner.markUserAsNotArrived}?userEventId=${user.userEventId}`,
        method: "put",
        globalIsLoading: true,
        invalidateKeys: [
          [endpointsMetadata.planner.getEvent],
          [endpointsMetadata.user.getUser],
        ],
      });
      sentUserEventIds.current.delete(user.userEventId);
    } catch {
      // already shown by useApiMutation
    }
  };

  const onQuickArrivalClick = async (user: IPlannerEventUserModel) => {
    if (!event || sentUserEventIds.current.has(user.userEventId)) {
      return;
    }

    const request = buildQuickArrivalRequest(
      user,
      event.id,
      event.price,
      studioSettings?.paymentTypes,
    );

    if (!request) {
      getErrorToast(
        "Няма начин на плащане за бързо отбелязване. Отбележете присъствието от страницата за пристигане.",
      );
      return;
    }

    sentUserEventIds.current.add(user.userEventId);

    try {
      await mutateAsync({
        endpoint: endpointsMetadata.planner.markUserAsArrived,
        method: "put",
        queryParams: request,
        globalIsLoading: true,
        disableSuccessToast: true,
        invalidateKeys: [
          [endpointsMetadata.planner.getEvent],
          [endpointsMetadata.user.getUser],
        ],
      });
    } catch {
      // already shown by useApiMutation
      sentUserEventIds.current.delete(user.userEventId);
      return;
    }

    const paidWith =
      request.paymentTypeId === PaymentTypes.Plan
        ? ` с карта „${user.currentPlan?.name}“.`
        : `: ${PaymentTypesLabels[request.paymentTypeId]}, ${formatPrice(request.price)}.`;

    getUndoToast(`Присъствието на ${user.name} е отбелязано${paidWith}`, () =>
      undoArrival(user),
    );
  };

  return { isQuickArrivalEnabled, onQuickArrivalClick };
};
